// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import AddIcon from '@mui/icons-material/Add'
import { Checkbox } from '@mui/material'
import cn from 'classnames'
import { groupBy } from 'lodash'
import moment from 'moment'
import React from 'react'
import { UserType } from '../auth/types'
import { i18n } from '../index'
import css from './expandableEmployeeRow.css'
type EmployeeRow = {
  endTime: string
  isFirstActivity: boolean
  note: string
  position: string
  responsibility: string[]
  salesIncentiveActual: number
  salesIncentiveTarget: number
  startTime: string
  startDateTime?: string
  endDateTime?: string
  thumbsUpActual: number
  thumbsUpTarget: number
  user: {
    name: string
  }
  userId: string
}
type Props = {
  rows: EmployeeRow[]
  expand: (...args: Array<any>) => any
  firstRowIndex: number
  submit: (...args: Array<any>) => any
  valueChanged: (...args: Array<any>) => any
  userType: UserType
  isToday: boolean
  employeeIncentives: {
    sia: number
    sit: number
    tua: number
    tut: number
  }
}

const ExpandableEmployeeRow = ({
  rows,
  expand,
  firstRowIndex,
  submit,
  userType,
  valueChanged,
  isToday,
  employeeIncentives,
}: Props) => {
  const getName = (): string => {
    return rows[0].user.name
  }

  const getPositions = (): string[] => {
    const positions = Object.keys(groupBy(rows, 'position'))
    return positions.map((pos) => i18n.t(`staffingPlan.positions.${pos}`))
  }

  const getResponsibilities = (): string[] => {
    return rows[0].responsibility.filter((res) => res !== 'Other')
  }

  const onBreakCheckboxChecked = async (add: boolean) => {
    const breaks = rows[0].breaks
    const keptBreaks = add ? breaks + 1 : breaks - 1
    await valueChanged(firstRowIndex, 'breaks', keptBreaks)
    submit()
  }

  const renderBreakCheckboxes = () => {
    const breaks = rows[0].breaks
    const keptBreaks = breaks | 0
    return [
      <Checkbox
        key={`breaks_1_${firstRowIndex}`}
        checked={keptBreaks > 0}
        disabled={!isToday}
        size={'small'}
        onChange={(e) => onBreakCheckboxChecked(e.target.checked)}
        color={'primary'}
      />,
      <Checkbox
        key={`breaks_2_${firstRowIndex}`}
        checked={keptBreaks > 1}
        disabled={keptBreaks < 1 || !isToday}
        size={'small'}
        onChange={(e) => onBreakCheckboxChecked(e.target.checked)}
        color={'primary'}
      />,
      <Checkbox
        key={`breaks_3_${firstRowIndex}`}
        checked={keptBreaks > 2}
        disabled={keptBreaks < 2 || !isToday}
        size={'small'}
        onChange={(e) => onBreakCheckboxChecked(e.target.checked)}
        color={'primary'}
      />,
    ]
  }
  const { startTime, endTime } = getTimes(rows)
  const { sia, sit, tua, tut } = employeeIncentives
  const salesIncentiveTargetReachedClass =
    rows[0].salesIncentiveActual >= rows[0].salesIncentiveTarget ? css.targetReached : css.targetNotReached
  const thumbsUpTargetReachedClass =
    rows[0].thumbsUpActual >= rows[0].thumbsUpTarget ? css.targetReached : css.targetNotReached
  return (
    <React.Fragment>
      <tr className={cn(css.listRow, css.listRowHighlighted)}>
        <td className={css.tableStaffName}>
          <div className={css.nameAndButton}>
            {getName()}
            <AddIcon onClick={expand} />
          </div>
        </td>
        <td>
          <div className={css.checkboxes}>{renderBreakCheckboxes()}</div>
        </td>
        <td className={css.tableTime}>{`${startTime} - ${endTime}`}</td>
        <td className={css.paddedCell}>{getPositions().join(', ')}</td>
        <td className={css.paddedCell}>{getResponsibilities().join(', ')}</td>

        {userType !== 'User' && (
          <td className={css.incentives}>
            <span className={salesIncentiveTargetReachedClass}>{`${sia} / ${sit || 0}`}</span>
          </td>
        )}

        {userType !== 'User' && (
          <td className={css.incentives}>
            <span className={thumbsUpTargetReachedClass}>{`${tua} / ${tut || 0}`}</span>
          </td>
        )}
      </tr>
    </React.Fragment>
  )
}

const getTimesOriginal = (rows: EmployeeRow[]): { startTime: string; endTime: string } => {
  const activityStartTimes = rows.map((row) => row.startTime)
  const activityEndTimes = rows.map((row) => row.endTime)
  const startTime = activityStartTimes.find((time) => !activityEndTimes.includes(time))
  const endTime = activityEndTimes.find((time) => !activityStartTimes.includes(time))
  if (!startTime || !endTime) {
    return {
      startTime: rows[0].startTime,
      endTime: rows[rows.length - 1].endTime,
    }
  }
  return {
    startTime,
    endTime,
  }
}

const getTimes = (
  rows: EmployeeRow[],
): {
  startTime: string
  endTime: string
} => {
  const someActivityIsMissingDateTimes = rows.some(
    (row) =>
      !row.startDateTime ||
      !row.endDateTime ||
      !moment(row.startDateTime).isValid() ||
      !moment(row.endDateTime).isValid(),
  )
  if (someActivityIsMissingDateTimes) {
    return getTimesOriginal(rows)
  }
  const activityStartTimes = rows.map((row) => moment(row.startDateTime!))
  const activityEndTimes = rows.map((row) => moment(row.endDateTime!))
  const startTimeMoment = moment.min(activityStartTimes)
  const endTimeMoment = moment.max(activityEndTimes)
  return {
    startTime: startTimeMoment.format('HH:mm'),
    endTime: endTimeMoment.format('HH:mm'),
  }
}

export default ExpandableEmployeeRow
